import type { CSSProperties } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const publicBase = process.env.PUBLIC_URL ?? '';

/** Raster brand assets in `public/brand/` (served at `/brand/…`). */
export const RELIX_MARK_SRC = `${publicBase}/brand/relix-mark.png`;
/** Dark / default header lockup (light backgrounds in dark UI use different treatment via CSS if needed). */
export const RELIX_LOCKUP_SRC = `${publicBase}/brand/relix-lockup-.png`;
export const RELIX_LOCKUP_SRC_LIGHT = `${publicBase}/brand/relix-lockup-light.png`;

export type RelixLogoProps = {
  size?: number;
  showWordmark?: boolean;
  className?: string;
  markClassName?: string;
  wordmarkClassName?: string;
  variant?: 'mark' | 'lockup' | 'wordmark';
};

type RelixMarkProps = {
  size?: number;
  className?: string;
  /** When true (default), the mark is treated as decorative (empty alt, aria-hidden). */
  decorative?: boolean;
};

export function RelixMark({ size = 36, className, decorative = true }: RelixMarkProps) {
  return (
    <img
      src={RELIX_MARK_SRC}
      alt={decorative ? '' : 'Relix'}
      aria-hidden={decorative}
      width={size}
      height={size}
      className={className}
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        display: 'block',
        flexShrink: 0,
      }}
      decoding="async"
    />
  );
}

type RelixLockupProps = {
  width?: number;
  height?: number;
  className?: string;
  style?: CSSProperties;
};

export function RelixLockup({ width, height, className, style }: RelixLockupProps) {
  const { theme } = useTheme();
  const src = theme === 'light' ? RELIX_LOCKUP_SRC_LIGHT : RELIX_LOCKUP_SRC;

  const merged: CSSProperties = {
    objectFit: 'contain',
    display: 'block',
    maxWidth: '100%',
    ...style,
  };
  if (width != null) merged.width = width;
  if (height != null) merged.height = height;

  return <img src={src} alt="Relix" className={className} style={merged} decoding="async" />;
}

type RelixWordmarkProps = {
  className?: string;
};

/** Renders “Relix” as text — use when the SVG lockup is not appropriate. */
export function RelixWordmark({ className }: RelixWordmarkProps) {
  return <span className={['relix-wordmark', className].filter(Boolean).join(' ')}>Relix</span>;
}

export function RelixLogo({
  size = 34,
  showWordmark = true,
  className,
  markClassName,
  wordmarkClassName,
  variant,
}: RelixLogoProps) {
  if (variant === 'wordmark') {
    return <RelixWordmark className={className} />;
  }

  if (variant === 'lockup') {
    return (
      <div className={className} style={{ display: 'inline-flex', alignItems: 'center' }}>
        <RelixLockup height={size} className={markClassName} />
      </div>
    );
  }

  if (variant === 'mark' || !showWordmark) {
    return (
      <RelixMark size={size} className={[className, markClassName].filter(Boolean).join(' ')} />
    );
  }

  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.65rem',
      }}
    >
      <RelixMark size={size} className={markClassName} decorative />
      <RelixWordmark className={wordmarkClassName} />
    </div>
  );
}
